import { initializeApp, FirebaseOptions } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithCustomToken as fbSignInWithCustomToken,
  signInWithEmailAndPassword as fbSignInWithEmailAndPassword,
  signInWithPhoneNumber as fbSignInWithPhoneNumber,
  getRedirectResult,
  UserCredential,
  IdTokenResult,
  ConfirmationResult,
  PhoneAuthProvider,
  linkWithCredential as fbLinkWithCredential,
  updatePhoneNumber as fbUpdatePhoneNumber,
  AuthCredential,
  NextOrObserver,
  PhoneAuthCredential,
  User,
} from 'firebase/auth';
import { ErrorFn, Unsubscribe } from '@firebase/util';

// Firebase conf
// see: https://firebase.google.com/docs/web/setup
// Init Firebase instance
export const firebaseApp = initializeApp(
  process.env.FIREBASE_CONFIG as FirebaseOptions,
);

export const getCurrentFbUser = () => getAuth(firebaseApp).currentUser;

export const signOutFbUser = (): Promise<void> =>
  getAuth(firebaseApp).signOut();

export const signInWithCustomToken = (token): Promise<UserCredential> =>
  fbSignInWithCustomToken(getAuth(firebaseApp), token);

export const handleAuthStateChanged = (
  onComplete: NextOrObserver<User>,
  onError?: ErrorFn,
): Unsubscribe => onAuthStateChanged(getAuth(firebaseApp), onComplete, onError);

export const handleIdTokenChanged = (
  onComplete: (IdTokenResult) => void,
): Unsubscribe =>
  handleAuthStateChanged((user) => {
    user
      ?.getIdTokenResult(true)
      .then((result: IdTokenResult) => onComplete(result));
  });

export const signInWithEmailAndPassword = (
  email,
  password,
): Promise<UserCredential> =>
  fbSignInWithEmailAndPassword(getAuth(firebaseApp), email, password);

declare global {
  interface Window {
    recaptchaVerifier: RecaptchaVerifier;
  }
}

export const createRecaptchaVerifier = (buttonId): RecaptchaVerifier => {
  window.recaptchaVerifier = new RecaptchaVerifier(
    buttonId,
    {
      size: 'invisible',
    },
    getAuth(firebaseApp),
  );
  return window.recaptchaVerifier;
};

export const signInWithPhoneNumber = (
  phoneNumber: string,
  appVerifier: RecaptchaVerifier,
): Promise<ConfirmationResult> => {
  getAuth(firebaseApp).useDeviceLanguage();
  return fbSignInWithPhoneNumber(
    getAuth(firebaseApp),
    phoneNumber,
    appVerifier,
  );
};

export const verifyPhoneAuthCode = (
  confirmationResult: ConfirmationResult,
  verificationCode: string,
): PhoneAuthCredential => {
  return PhoneAuthProvider.credential(
    confirmationResult.verificationId,
    verificationCode,
  );
};

export const linkWithCredential = (
  credential: AuthCredential,
): Promise<string> => {
  return fbLinkWithCredential(
    getAuth(firebaseApp).currentUser as User,
    credential,
  ).then((credential: UserCredential) => credential.user.getIdToken(true));
};

export const updatePhoneNumber = (
  credential: PhoneAuthCredential,
): Promise<string | null | undefined> => {
  return fbUpdatePhoneNumber(
    getAuth(firebaseApp).currentUser as User,
    credential,
  ).then(() => getAuth(firebaseApp).currentUser?.getIdToken(true));
};

export const linkOrUpdatePhoneNumber = (
  phoneCredential: PhoneAuthCredential,
): Promise<string | null | undefined> => {
  return isLinkedProvider('phone')
    ? updatePhoneNumber(phoneCredential)
    : linkWithCredential(phoneCredential);
};

export const isLinkedProvider = (providerId: string) => {
  const index = getAuth(firebaseApp).currentUser?.providerData?.findIndex(
    (data) => data.providerId === providerId,
  );
  return index != null && index > -1;
};

export const getAuthRedirectResult = (): Promise<UserCredential | null> =>
  getRedirectResult(getAuth(firebaseApp));
